import { useEffect, useState } from 'react';
import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { extractErrorMessage } from '@/lib/api';
import { ErrorScreen } from './ErrorScreen';
import { ErrorDetailsModal } from './ErrorDetailsModal';

function getErrorTitle(error: unknown, t: TFunction<'errors'>): string {
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) return t('router.pageNotFound');
    if (error.status === 401) return t('router.unauthorizedTitle');
    if (error.status === 403) return t('router.accessDenied');
    if (error.status >= 500) return t('router.serverErrorTitle');
    return t('router.requestError');
  }
  return t('router.somethingWentWrong');
}

function getErrorMessage(error: unknown, t: TFunction<'errors'>): string {
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) return t('router.pageMissingDescription');
    return error.statusText || t('generic');
  }
  return extractErrorMessage(error, t('generic'));
}

interface RouterErrorBoundaryProps {
  /**
   * Render the 404 screen without a thrown error. Used by the catch-all route:
   * an unmatched URL is an ordinary navigation, not an exception, so there is
   * no error object to report or toast about.
   */
  notFound?: boolean;
}

/**
 * `errorElement` for every route, and the element behind the catch-all `*`
 * route. Without this, React Router falls back to its own developer-facing
 * "Unexpected Application Error!" screen.
 */
export function RouterErrorBoundary({ notFound = false }: RouterErrorBoundaryProps) {
  const routeError = useRouteError();
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);
  const { t } = useTranslation('errors');

  const error = notFound ? null : routeError;
  const is404 = notFound || (isRouteErrorResponse(error) && error.status === 404);
  const hasError = !notFound && error !== undefined && error !== null;

  const title = notFound ? t('router.pageNotFound') : getErrorTitle(error, t);
  const message = notFound ? t('router.pageMissingDescription') : getErrorMessage(error, t);

  useEffect(() => {
    if (!hasError) return;

    if (import.meta.env.DEV) {
      console.error('RouterErrorBoundary caught an error:', error);
    }

    // The Toaster is mounted outside the router, so this survives the error
    // screen unmounting -- dismiss it explicitly on the way out.
    const toastId = toast.error(title, { description: message, duration: 10000 });
    return () => {
      toast.dismiss(toastId);
    };
  }, [hasError, error, title, message]);

  return (
    <>
      <ErrorScreen is404={is404} title={title} message={message}>
        <button type="button" onClick={() => navigate('/')} className="btn-primary">
          {t('router.goHome')}
        </button>
        {!is404 && (
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="btn-secondary"
          >
            {t('tryAgain')}
          </button>
        )}
        {hasError && (
          <button
            type="button"
            onClick={() => setShowDetails(true)}
            className="btn-secondary"
          >
            {t('router.viewDetails')}
          </button>
        )}
      </ErrorScreen>

      {hasError && (
        <ErrorDetailsModal
          error={error}
          isOpen={showDetails}
          onClose={() => setShowDetails(false)}
        />
      )}
    </>
  );
}
